"use client";

import { useEffect, useState, useTransition } from "react";
import { setThemeAction } from "@/lib/theme/actions";
import { isThemeMode, type ThemeMode } from "@/lib/theme/types";
import { Icon, type IconName } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { themeToggleTranslations } from "./ThemeToggle.i18n";
import styles from "./ThemeToggle.module.css";

/**
 * 🌓 Theme toggle — przełącznik trybu motywu (light -> dark -> hc -> light).
 *
 * **Lokalizacja:** prawa strona `<HDefault />` (nagłówek strony publicznej),
 * NIE w admin top barze. Theme to user preference dla CMS frontendu;
 * admin chrome zawsze ma swój dark navy theme niezależny od tego.
 *
 * Strategia (zob. `.claude/CLAUDE.md` sekcja "Tryby motywu"):
 *   - Wybór persistowany w cookie `cms_theme` (Server Action `setThemeAction`)
 *   - Atrybut `data-theme` na `<html>` ustawiany w root layout (Server Component
 *     czyta cookie, pisze atrybut -> CSS vars w `:root[data-theme="X"]` aktywne)
 *   - Komponenty NIE zmieniają się — wszystkie używają `var(--color-primary)`
 *
 * Initial state z `document.documentElement.dataset.theme` (po hydration) —
 * unika hydration mismatch dla SSR-renderowanego atrybutu.
 *
 * **TODO:** plik leży w `components/admin/AdminTopBar/partials/` z historycznych
 * powodów (był pierwotnie w admin barze). Rozważ przeniesienie do
 * `components/theme/ThemeToggle/` przy następnym refactor'ze.
 */
export type ThemeToggleProps = {
  /** Lista motywów dostępnych dla odwiedzającego — wynikająca z admin
   *  toggle'ów w `cms-config` (`enable_funeral_version`, `enable_dark_version`,
   *  `enable_cms_contrast_version`). `light` zawsze dostępny (default).
   *  Gdy lista zawiera TYLKO `light` -> komponent nie renderuje nic
   *  (nie ma potrzeby pokazywać przełącznika z jednym wyborem). */
  availableThemes: ThemeMode[];
};

export function ThemeToggle({ availableThemes }: ThemeToggleProps) {
  const [theme, setTheme] = useState<ThemeMode>("light");
  const [, startTransition] = useTransition();
  const t = useT(themeToggleTranslations);

  /* Cycle order — light zawsze pierwszy (default), pozostałe w stałej
     kolejności (dark -> funeral -> hc) żeby UX był przewidywalny niezależnie
     od tego które motywy admin włączył. */
  const cycle: ThemeMode[] = ["light", "dark", "funeral", "hc"].filter(
    (m): m is ThemeMode => availableThemes.includes(m as ThemeMode)
  );

  //Initial sync z DOM (po hydration). Server Component już ustawił atrybut.
  useEffect(() => {
    const current = document.documentElement.dataset.theme;
    if (isThemeMode(current) && cycle.includes(current)) {
      setTheme(current);
    } else {
      /* Jeśli cookie wskazuje motyw który admin właśnie wyłączył —
         resetujemy do `light` (zawsze dostępne). DOM też czyścimy żeby
         CSS variables wróciły do baseline. */
      setTheme("light");
      delete document.documentElement.dataset.theme;
    }
  }, [cycle]);

  /* Gdy tylko `light` -> nie renderujemy przełącznika (single option = no
     switcher). Decyzja podejmowana po hooks żeby zachować stabilny order. */
  if (cycle.length <= 1) {
    return null;
  }

  const handleClick = () => {
    const idx = cycle.indexOf(theme);
    const next = cycle[(idx + 1) % cycle.length];

    //Optimistic: aktualizuj atrybut DOM natychmiast
    setTheme(next);
    if (next === "light") {
      delete document.documentElement.dataset.theme;
    } else {
      document.documentElement.dataset.theme = next;
    }

    //Background: persist do cookie
    startTransition(async () => {
      await setThemeAction(next);
    });
  };

  const icon = THEME_ICON[theme];
  const themeLabel = t.themeName[theme];

  return (
    <button
      type="button"
      className={styles.toggle}
      onClick={handleClick}
      title={t.tooltip(themeLabel)}
      aria-label={t.ariaLabel(themeLabel)}
    >
      <Icon name={icon} className={styles.icon} />
    </button>
  );
}

const THEME_ICON: Record<ThemeMode, IconName> = {
  light: "sun",
  dark: "moon",
  funeral: "flower-lotus",
  hc: "circle-half",
};
